import { useContext, useEffect } from 'react';
import { Button } from '@fff-web/fff-ui-shared';
import { useUserContext } from '@fff-web/fff-utilities';
import { eventCancelFlowStarted } from '../../utils/segment/eventsHandlers';
import { SubscriptionType } from '../container/Container';
import { useKeepSubscriptionsDataContext } from '../context/KeepSubscriptionsContext';
import { SurveyPageContext } from '../context/SurveyPageContext';
import { fireMembershipTrackEvent } from './fireMembershipTrackEvent';
import { KeepSubscriptionsLoadingFallback } from './KeepSubscriptionsLoadingFallback';
import { ProductCard } from './ProductCard';
import { useKeepSubscriptionsErrorFallback } from './useKeepSubscriptionsErrorFallback';
import * as styles from './KeepSubscriptions.module.scss';

/* ------------------------------------------------------------------------------------------------
 * Constants
 * ----------------------------------------------------------------------------------------------- */

export const LABELS = {
  TITLE: 'Wait, You Have Active reFill(s) Shipping With Your Box!',
  DISCLAIMER: {
    MAIN: 'If you click "Cancel Everything", your Shipping With Box reFills will expire at the end of your membership period. You may keep these reFills and all membership perks by clicking "Keep Selected Subscriptions", and we will switch them to a 90-day shipping cadence when your box subscription expires.',
    NOTE: 'Please note:',
    ADDITIONAL: '90-day shipment orders that fall below $49 will be charged a $6.95 shipping fee.',
  },
  BUTTONS: {
    KEEP: 'KEEP SELECTED SUBSCRIPTIONS',
    CANCEL: 'CANCEL EVERYTHING',
  },
  SUBSCRIPTION_INFO: 'View the My Subscriptions page to confirm when your next shipment is.',
  START_EVENT_NAME: 'Membership Save Started',
  COMPLETE_EVENT_NAME: 'Membership Save Completed',
};

/* ------------------------------------------------------------------------------------------------
 * Sub-components
 * ----------------------------------------------------------------------------------------------- */

const Title = () => <h1 className={styles.title}>{LABELS.TITLE}</h1>;

const Disclaimer = () => {
  return (
    <div className={styles.disclaimerContainer}>
      <p>{LABELS.DISCLAIMER.MAIN}</p>
      <br />
      <p className={styles.disclaimerNote}>
        <span>{LABELS.DISCLAIMER.NOTE}</span> {LABELS.DISCLAIMER.ADDITIONAL}
      </p>
    </div>
  );
};

/* ------------------------------------------------------------------------------------------------
 * Main component
 * ----------------------------------------------------------------------------------------------- */

const KeepSubscriptions = () => {
  const { increasePage } = useContext(SurveyPageContext);
  const {
    status,
    goWithBoxProductSubscriptions,
    selectedProducts,
    toggleProductSelection,
    postSwitchGwbSubscriptionsCadenceBodyRequest,
    setUserHasKeptSubscriptions,
  } = useKeepSubscriptionsDataContext();
  const showLoadingFallback = status === 'loading' || status === 'error';
  const noProductsSelected = postSwitchGwbSubscriptionsCadenceBodyRequest.length === 0;
  const { userInfo } = useUserContext();
  const subscriptionType = userInfo?.user?.subscriptionType as SubscriptionType;

  useKeepSubscriptionsErrorFallback();

  useEffect(() => {
    eventCancelFlowStarted(subscriptionType);
    fireMembershipTrackEvent(LABELS.START_EVENT_NAME);
  }, []);

  const onCTAClick = (type: 'keep' | 'cancel') => {
    if (type === 'keep') {
      fireMembershipTrackEvent(LABELS.COMPLETE_EVENT_NAME, LABELS.BUTTONS.KEEP);

      setUserHasKeptSubscriptions(true);
    }

    increasePage();
  };

  const Products = () => (
    <div className={styles.productsContainer}>
      {goWithBoxProductSubscriptions?.map((product) => (
        <ProductCard
          key={product.id}
          isSelected={selectedProducts[product.id] ?? true}
          onToggle={() => toggleProductSelection(product.id)}
          {...product}
        />
      ))}
    </div>
  );

  const Footer = () => (
    <div className={styles.buttonsContainer}>
      <div className={styles.mainButtonContainer}>
        <Button
          onClick={() => onCTAClick('keep')}
          disabled={showLoadingFallback || noProductsSelected}
        >
          {LABELS.BUTTONS.KEEP}
        </Button>
        <span>{LABELS.SUBSCRIPTION_INFO}</span>
      </div>
      <Button
        onClick={() => onCTAClick('cancel')}
        variation="black-outline"
        disabled={showLoadingFallback}
      >
        {LABELS.BUTTONS.CANCEL}
      </Button>
    </div>
  );

  return (
    <div className={styles.keepSubscriptionsContainer}>
      <Title />
      <Disclaimer />
      {showLoadingFallback && <KeepSubscriptionsLoadingFallback />}
      {status === 'success' && <Products />}
      <Footer />
    </div>
  );
};

export { KeepSubscriptions };
